// Parse a coding-problem URL (LeetCode, GeeksforGeeks, Codeforces, HackerRank,
// InterviewBit, etc.) into a human-friendly title + a detected source label.
//
// This powers the "just paste a link" add-problem flow: the user drops a URL,
// we guess the title and platform, and they can tweak the rest (topic,
// difficulty) before saving. Everything here is pure + side-effect free so it
// is trivially testable.

const SOURCES = [
	{ host: "leetcode.com", label: "LeetCode" },
	{ host: "leetcode.cn", label: "LeetCode" },
	{ host: "geeksforgeeks.org", label: "GeeksforGeeks" },
	{ host: "codeforces.com", label: "Codeforces" },
	{ host: "hackerrank.com", label: "HackerRank" },
	{ host: "hackerearth.com", label: "HackerEarth" },
	{ host: "interviewbit.com", label: "InterviewBit" },
	{ host: "codechef.com", label: "CodeChef" },
	{ host: "spoj.com", label: "SPOJ" },
	{ host: "atcoder.jp", label: "AtCoder" },
];

// path segments that carry no title information
const NOISE = new Set(["description", "solution", "solutions", "editorial", "discuss", "submissions", "problems", "problem", "problemset", "challenges", "1", "0"]);

export function detectSource(hostname) {
	const host = (hostname || "").replace(/^www\./, "").toLowerCase();
	const match = SOURCES.find((s) => host === s.host || host.endsWith("." + s.host));
	return match ? match.label : "";
}

// Turn a url slug ("longest-substring") into a title ("Longest Substring").
export function slugToTitle(slug) {
	return (slug || "")
		.replace(/-\d{6,}$/, "") // strip trailing GfG-style numeric hash, e.g. "-1587115620"
		.replace(/[-_]+/g, " ")
		.replace(/%20/g, " ")
		.replace(/\s+/g, " ")
		.trim()
		.split(" ")
		.map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
		.join(" ");
}

// pick the most title-like segment from a pathname
function meaningfulSegment(pathname) {
	const segs = (pathname || "").split("/").filter(Boolean);

	// Prefer the segment immediately after a "problems"/"problem" marker
	// (LeetCode, GfG practice, HackerRank, InterviewBit all use this).
	const markerIdx = segs.findIndex((s) => s.toLowerCase() === "problems" || s.toLowerCase() === "problem");
	if (markerIdx !== -1 && segs[markerIdx + 1]) {
		return segs[markerIdx + 1];
	}

	// Otherwise walk back from the end, skipping noise / pure-numeric segments.
	for (let i = segs.length - 1; i >= 0; i--) {
		const s = segs[i];
		if (NOISE.has(s.toLowerCase()) || /^\d+$/.test(s)) continue;
		return s;
	}
	return segs.length ? segs[segs.length - 1] : "";
}

// Main entry: returns { url, problem, source }.
export function parseProblemUrl(input) {
	const url = (input || "").trim();
	if (!url) return { url: "", problem: "", source: "" };

	let parsed;
	try {
		parsed = new URL(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(url) ? url : "https://" + url);
	} catch (e) {
		// Not a parseable URL — best-effort title from the raw text.
		return { url, problem: slugToTitle(meaningfulSegment(url)), source: "" };
	}

	const source = detectSource(parsed.hostname);
	const slug = meaningfulSegment(parsed.pathname);
	return { url, problem: slugToTitle(slug), source };
}

export default parseProblemUrl;
